"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import {
  MonitoringTrendChart,
  MONITORING_METRICS,
  type MonitoringMetricKey,
} from "@/components/cards/MonitoringTrendChart";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const METRIC_DOT_COLORS: Record<MonitoringMetricKey, string> = {
  overall: "#3b82f6",
  performance: "#22c55e",
  quality: "#f59e0b",
  trust: "#ef4444",
  dr: "#8b5cf6",
};

interface CertifiedChartProps {
  data: Array<{
    date: string;
    overall: number | null;
    performance?: number | null;
    quality?: number | null;
    trust?: number | null;
    dr?: number | null;
  }>;
}

export function CertifiedChart({ data }: CertifiedChartProps) {
  const [activeMetric, setActiveMetric] =
    React.useState<MonitoringMetricKey>("overall");

  const availableMetrics = React.useMemo(() => {
    const METRIC_ORDER: MonitoringMetricKey[] = [
      "overall",
      "performance",
      "quality",
      "trust",
      "dr",
    ];

    return METRIC_ORDER.filter((metric) =>
      data.some((point) => typeof point[metric] === "number")
    );
  }, [data]);

  const indicatorLabel = MONITORING_METRICS[activeMetric]?.label ?? "";

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          Score trend
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-2 py-1 text-[11px] font-medium text-muted-foreground/90 shadow-sm backdrop-blur transition-all hover:border-border hover:bg-background/90 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                aria-hidden
                style={{
                  backgroundColor: METRIC_DOT_COLORS[activeMetric],
                }}
              />
              <span>{indicatorLabel}</span>
              <ChevronDown
                className="h-3.5 w-3.5 text-muted-foreground/70"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {availableMetrics.map((metricKey) => {
              const metricConfig = MONITORING_METRICS[metricKey];
              const isActive = metricKey === activeMetric;
              return (
                <DropdownMenuItem
                  key={metricKey}
                  onSelect={() => setActiveMetric(metricKey)}
                  className="flex items-center gap-2 text-sm"
                  data-state={isActive ? "checked" : undefined}
                >
                  <span
                    className="inline-flex h-2 w-2 rounded-full"
                    aria-hidden
                    style={{
                      backgroundColor: METRIC_DOT_COLORS[metricKey],
                    }}
                  />
                  <span
                    className={cn(
                      isActive ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {metricConfig.label}
                  </span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <MonitoringTrendChart data={data} metric={activeMetric} />
    </div>
  );
}
